/**
 * Cart drawer recommendations: loads products related to the first cart item through the
 * Product Recommendations endpoint (rendered by sections/cart-upsell.liquid), shows them in a
 * scroll-snap slider and adds them to the cart without leaving the drawer.
 */

if (!customElements.get('cart-upsell')) {
  class CartUpsell extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-cart-upsell-step]');
        if (step) return this.scrollTrack(Number(step.dataset.cartUpsellStep));

        const add = event.target.closest('button[data-variant-id]');
        if (add) this.add(add);
      });

      this.load();
    }

    async load() {
      const productId = this.dataset.productId;
      if (!productId) {
        this.dataset.state = 'empty';
        this.setAttribute('aria-busy', 'false');
        return;
      }

      this.dataset.state = 'loading';
      this.setAttribute('aria-busy', 'true');

      const markup = await this.fetchMarkup(productId);
      if (!this.isConnected) return;

      // The section filters against the current cart, so refresh it after each drawer render.
      this.innerHTML = markup;
      const inCart = new Set((this.dataset.cartProductIds || '').split(','));
      this.querySelectorAll('.cart-upsell__item').forEach((item) => {
        if (inCart.has(item.dataset.productId)) item.remove();
      });

      this.hidden = this.querySelectorAll('.cart-upsell__item').length === 0;
      this.dataset.state = this.hidden ? 'empty' : 'ready';
      this.setAttribute('aria-busy', 'false');
      this.track = this.querySelector('.cart-upsell__track');
      if (this.hidden || !this.track) return;

      this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });
      this.updateArrows();
    }

    async fetchMarkup(productId) {
      try {
        const response = await fetch(`${this.dataset.url}&product_id=${encodeURIComponent(productId)}`);
        if (!response.ok) return '';
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        return html.querySelector('.cart-upsell__inner')?.outerHTML || '';
      } catch (error) {
        console.error(error);
        return '';
      }
    }

    scrollTrack(direction) {
      if (!this.track) return;
      this.track.scrollBy({ left: direction * this.track.clientWidth * 0.85, behavior: 'smooth' });
    }

    updateArrows() {
      const max = this.track.scrollWidth - this.track.clientWidth - 1;
      this.querySelector('.cart-upsell__arrow--prev').disabled = this.track.scrollLeft <= 0;
      this.querySelector('.cart-upsell__arrow--next').disabled = this.track.scrollLeft >= max;
    }

    async add(button) {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || button.getAttribute('aria-disabled') === 'true') return;

      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');
      const errorMessage = drawer.querySelector('#CartDrawer-CartErrors');
      if (errorMessage) errorMessage.textContent = '';

      const body = new FormData();
      body.append('id', button.dataset.variantId);
      body.append('quantity', '1');
      body.append(
        'sections',
        drawer.getSectionsToRender().map((section) => section.id),
      );
      body.append('sections_url', window.location.pathname);

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = body;

      let added = false;
      try {
        const result = await fetch(routes.cart_add_url, config);
        const response = await result.json();
        if (!result.ok || response.status) {
          const message = response.description || window.cartStrings.error;
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'cart-upsell',
            productVariantId: button.dataset.variantId,
            errors: response.errors || response.description,
            message: response.message,
          });
          if (errorMessage) errorMessage.textContent = message;
          return;
        }
        added = true;

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-upsell',
          productVariantId: button.dataset.variantId,
          cartData: response,
        });
        drawer.renderContents(response);
      } catch (error) {
        if (added) {
          window.location.assign(routes.cart_url);
        } else if (errorMessage) {
          errorMessage.textContent = window.cartStrings.error;
        }
      } finally {
        if (!added) button.removeAttribute('aria-disabled');
        button.classList.remove('is-loading');
      }
    }
  }

  customElements.define('cart-upsell', CartUpsell);
}
